/*
 * Parla Oral Care — Free Gift: Paste Press Toothpaste Key
 *
 * Adds the Paste Press Key (free) when a qualifying 3-pack / Discovery Kit is
 * ADDED to the cart. Pairs with the free-gift discount configured in Shopify
 * admin, which prices the Key at £0.
 *
 * Qualifying is matched on product_id, so it covers one-time AND subscription
 * purchases of the same product — a selling plan changes the variant and price,
 * never the product_id.
 *
 * ---------------------------------------------------------------------------
 * DESIGN RULES — both of these come from real customer complaints, please keep
 * them in mind before "improving" the trigger logic:
 *
 * 1. THE GIFT IS OFFERED ONCE PER ADD, NEVER RE-ADDED.
 *    We only act on a cart ADD whose own response says a qualifying product
 *    went in. We deliberately do NOT act on /cart/change, /cart/update,
 *    /cart/clear, or on page load. That is what makes a customer's removal of
 *    the Key stick: previously any cart write (and every page load) re-ran a
 *    "cart contains qualifier but no Key → add it" check, so the Key kept
 *    reappearing the moment they deleted it, and adding an unrelated item or
 *    reloading brought it straight back.
 *
 * 2. WE NEVER REMOVE THE GIFT.
 *    Not even when the last qualifying product leaves the cart. Per the
 *    merchant: the free-gift discount stops applying on its own, and the line
 *    should just be left alone.
 *
 * 3. THE BUNDLE BUILDER IS A SEPARATE SYSTEM — STAY OUT OF IT.
 *    /pages/build-your-routine (bk-bundle-builder) builds the cart itself and
 *    adds its own £0 gift product, which needs no discount and so never
 *    competes with BUNDLE15/20. Its bulk /cart/add contains qualifying 3-packs,
 *    so without the bail-out below this script would add the PAID Key on top of
 *    the funnel's free one — two Keys, one of them charged. This script owns the
 *    normal storefront (PDP, cart drawer upsell); the funnel owns itself.
 * ---------------------------------------------------------------------------
 *
 * URL MATCHING: the theme posts to `routes.cart_add_url`, which Liquid renders
 * as `/cart/add` with NO `.js` suffix (and a locale prefix on non-primary
 * markets, e.g. `/en-gb/cart/add`). Skio's plan picker posts to `/cart/add.js`.
 * The matcher has to cover all of those shapes.
 */

(function () {
  'use strict';

  /*
   * DESIGN RULE 3 — do nothing at all on a page carrying the bundle builder.
   * #bkbb is that section's root element (see bk-bundle-builder.liquid). We are
   * loaded with `defer` at the end of <body>, so the DOM is parsed by now and
   * this check is reliable; keep the defer if this include is ever moved.
   * Bailing before the fetch patch leaves the funnel completely untouched.
   */
  if (document.getElementById('bkbb')) return;

  const FREE_GIFT_VARIANT_ID = 55548751446395; // Paste Press Toothpaste Key — Default Title
  const FREE_GIFT_PRODUCT_ID = 15140362551675; // Paste Press Toothpaste Key
  const FREE_GIFT_PROP = '_is_free_gift';

  const QUALIFYING_PRODUCT_IDS = [
    15161007964539, // Toothpaste Discovery Kit
    15158916710779, // Bio Balance Toothpaste x3
    15158915694971, // Enamel Protect Toothpaste x3
    15157001257339, // Pro Whitening Toothpaste x3
  ];

  // Captured before we patch window.fetch. Every request we make ourselves goes
  // through this, so our own cart writes can never re-trigger the interceptor.
  const nativeFetch = window.fetch.bind(window);

  // Adds only — see DESIGN RULE 1. Matches /cart/add, /cart/add.js,
  // /en-gb/cart/add, and any of those with a query string.
  const CART_ADD_RE = /\/cart\/add(?:\.js)?(?:[?#]|$)/;

  let checking = false;
  let recheckQueued = false;

  function numberOr(value, fallback) {
    return typeof value === 'number' ? value : fallback;
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    if (input && typeof input.href === 'string') return input.href;
    return '';
  }

  function isQualifier(productId) {
    return QUALIFYING_PRODUCT_IDS.includes(productId);
  }

  /*
   * Pull the product ids out of a /cart/add response. The theme's product form
   * gets back a single line-item object (Dawn reads `parsedState.id` from it);
   * bulk adds come back as { items: [...] }. Handle both, and if the shape is
   * something we don't recognise return nothing — failing to add the gift is a
   * visible, fixable problem, whereas guessing risks re-adding a Key the
   * customer just deleted, which is the bug we are fixing.
   */
  function addedProductIds(data) {
    if (!data) return [];
    const list = Array.isArray(data.items) ? data.items : [data];
    return list.map((item) => item && item.product_id).filter((id) => typeof id === 'number');
  }

  /* ------------------------------------------------------------------ *
   * Section rendering
   *
   * The gift goes in on a second request, after the theme has already
   * painted the drawer from its own add-to-cart response — so that paint
   * does not contain the gift. We therefore ask for the cart sections on
   * our own write and repaint them, so the Key shows up immediately
   * instead of on the next page load.
   *
   * Both cart UIs are honoured, each with its own contract:
   *   cart-drawer  → response keyed by section.id
   *   cart-items   → response keyed by section.section  (cart page only)
   *
   * `cart-icon-bubble` is deliberately skipped: this theme's bk-header renders
   * its own cart icon and [data-bk-cart-count] badge, so painting the stock
   * section into #cart-icon-bubble would replace the brand markup with Dawn's.
   * The count is updated in place by updateHeaderCount() instead.
   * ------------------------------------------------------------------ */
  const SKIP_SECTIONS = ['cart-icon-bubble'];

  function renderPlan() {
    const plan = [];

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        drawer.getSectionsToRender().forEach((section) => {
          if (SKIP_SECTIONS.includes(section.id)) return;
          plan.push({
            key: section.id,
            selector: section.selector,
            host: drawer,
            resolve: () =>
              section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id),
          });
        });
      } catch (err) {
        console.error('[Parla] free gift: cart drawer sections unavailable', err);
      }
    }

    // <cart-items> is the cart page only; <cart-drawer-items> is a different tag.
    const items = document.querySelector('cart-items');
    if (items && typeof items.getSectionsToRender === 'function') {
      try {
        items.getSectionsToRender().forEach((section) => {
          if (SKIP_SECTIONS.includes(section.section)) return;
          plan.push({
            key: section.section,
            selector: section.selector,
            host: items,
            resolve: () => {
              const container = document.getElementById(section.id);
              if (!container) return null;
              return container.querySelector(section.selector) || container;
            },
          });
        });
      } catch (err) {
        console.error('[Parla] free gift: cart page sections unavailable', err);
      }
    }

    return plan;
  }

  function applySections(sections) {
    if (!sections) return;

    renderPlan().forEach((entry) => {
      const html = sections[entry.key];
      if (!html) return;
      const target = entry.resolve();
      if (!target) return;
      try {
        target.innerHTML = entry.host.getSectionInnerHTML(html, entry.selector);
      } catch (err) {
        console.error('[Parla] free gift: could not render section', entry.key, err);
      }
    });

    rebindDrawerOverlay();
  }

  // #CartDrawer-Overlay lives *inside* #CartDrawer, so repainting that markup
  // drops the click-to-close listener bound in the drawer's constructor. Dawn
  // re-binds it in renderContents(); we have to do the same after our repaint,
  // or clicking outside the drawer stops closing it.
  function rebindDrawerOverlay() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
  }

  // We only ever add, so the cart is non-empty afterwards. These classes sit on
  // elements outside the repainted markup.
  function clearEmptyState() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('is-empty');

    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.remove('is-empty');
  }

  // See SKIP_SECTIONS: the header count is patched in place rather than by
  // repainting the stock cart-icon-bubble section over the brand markup.
  function updateHeaderCount(itemCount) {
    if (typeof itemCount !== 'number') return;

    document.querySelectorAll('[data-bk-cart-count]').forEach((el) => {
      el.textContent = itemCount;
      if (itemCount === 0) {
        el.setAttribute('hidden', '');
      } else {
        el.removeAttribute('hidden');
      }
    });

    // Dawn-style header, in case the theme ever falls back to one.
    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
      el.textContent = itemCount;
    });
  }

  async function addGiftToCart() {
    const body = {
      id: FREE_GIFT_VARIANT_ID,
      quantity: 1,
      properties: { [FREE_GIFT_PROP]: 'true' },
    };

    const keys = renderPlan()
      .map((entry) => entry.key)
      .filter(Boolean);
    if (keys.length) {
      body.sections = Array.from(new Set(keys)).join(',');
      body.sections_url = window.location.pathname;
    }

    const res = await nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const detail = (data && (data.description || data.message)) || '';
      throw new Error('/cart/add.js → ' + res.status + ' ' + detail);
    }
    return data;
  }

  /*
   * Called ONLY after a qualifying product was just added (see DESIGN RULE 1).
   * Adds the Key unless one is already in the cart.
   */
  async function offerFreeGift() {
    if (checking) {
      recheckQueued = true;
      return;
    }
    checking = true;

    try {
      const res = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const items = (cart && cart.items) || [];

      const hasQualifier = items.some((item) => isQualifier(item.product_id));
      const hasKey = items.some((item) => item.product_id === FREE_GIFT_PRODUCT_ID);

      // hasQualifier is re-checked here because the add we reacted to could have
      // been rolled back (sold out, quantity rules) between the two requests.
      if (!hasQualifier || hasKey) return;

      const result = await addGiftToCart();
      const nextCount = numberOr(result && result.item_count, (cart.item_count || 0) + 1);

      clearEmptyState();
      updateHeaderCount(nextCount);
      applySections(result && result.sections);

      document.dispatchEvent(
        new CustomEvent('parla:cart:updated', { bubbles: true, detail: { cart: result } })
      );
    } catch (err) {
      console.error('[Parla] free gift add failed:', err);
    } finally {
      checking = false;
      if (recheckQueued) {
        recheckQueued = false;
        offerFreeGift();
      }
    }
  }

  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    if (!CART_ADD_RE.test(urlOf(input))) return promise;

    return promise.then((response) => {
      if (!response || !response.ok) return response;

      // Read a CLONE — the caller still has to consume the original body.
      // Only a qualifying product going in may trigger the gift; adding
      // anything else must never resurrect a Key the customer deleted.
      response
        .clone()
        .json()
        .then((data) => {
          if (addedProductIds(data).some(isQualifier)) {
            // Fresh task, so the theme paints its own update first.
            setTimeout(offerFreeGift, 0);
          }
        })
        .catch(() => {
          /* non-JSON add response — nothing we can safely act on */
        });

      return response;
    });
  };

  // Manual hook for debugging from the console. Note this bypasses DESIGN RULE 1
  // (it does not require a qualifying add), so it can re-add a Key the customer
  // removed — for testing only, never wire it to a UI event.
  window.parlaFreeGiftOffer = offerFreeGift;
})();
